import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dices, Home, MoreVertical, RefreshCw, SkipForward } from 'lucide-react';
import ContextMenu from '../../../ui/ContextMenu';
import Tooltip from '../../../ui/Tooltip';
import DicePopover from './DicePopover';
import ActionSheet from './ActionSheet';
import { useActionConfirm } from '../hooks/useActionConfirm';
import { diceActions } from '../utils/cardActions';
import './ActionRail.css';

/**
 * Aktionsleiste des Bretts (Spec 2026-10-21 §5.5, §5.6): senkrechte Knopfreihe, am Rechner
 * links, am Handy schmal rechts.
 *
 *   <ActionRail onUntapAndDraw={untapAndDraw} onEndTurn={endTurn} canEndTurn={isMyTurn}
 *     onHome={camera.home} onRollDice={rollDice} maxHeight={availablePx}
 *     more={sessionActions({ isHost, playerStatus, onOverview, … })} />
 *
 * - Sichtbar in dieser Reihenfolge: Enttappen & Karte ziehen, Zug beenden, Eigene Fläche,
 *   Würfeln (öffnet `DicePopover`), darunter „Mehr". Ein Knopf, dessen Rückruf fehlt, entfällt
 *   (am Handy lässt die Seite `onEndTurn` weg, „Zug beenden" steht dort oben).
 *   `canEndTurn=false` sperrt „Zug beenden".
 * - „Mehr": Einträge aus `more` (Form wie `utils/cardActions.js`, typisch `sessionActions`).
 *   Rechner: Dropdown über `ui/ContextMenu`; Handy (`isMobile`): `ActionSheet`. Einträge mit
 *   `confirm` fragen per `ConfirmDialog` nach, bevor `onSelect` läuft.
 * - Überlauf: Mit `maxHeight` (px, verfügbare Höhe bis zum unteren Rand) wandern Knöpfe von
 *   unten her ins „Mehr"-Menü, solange die Leiste sonst höher würde; sie stehen dort vor den
 *   `more`-Einträgen, Würfeln als Untermenü. Gemessen wird die Höhe jedes Knopfes per
 *   `getBoundingClientRect` (zwischengespeichert, damit ausgelagerte Knöpfe zurückkehren
 *   können, sobald wieder Platz ist), dazu `row-gap` und Innenabstand der Leiste; ein
 *   `ResizeObserver` auf den Knöpfen rechnet neu, wenn sich deren Größe ändert. Ohne
 *   `maxHeight` gibt es keinen Überlauf.
 * - Prüfanker: `data-testid="action-rail"`, je Knopf `data-rail-item="<id>"`.
 */
export default function ActionRail({
  onUntapAndDraw, onEndTurn, canEndTurn = false, onHome, onRollDice, more = [], maxHeight, isMobile = false,
}) {
  const buttons = useMemo(() => [
    onUntapAndDraw && { id: 'untap-draw', label: 'Enttappen & Karte ziehen', icon: RefreshCw, onSelect: onUntapAndDraw },
    onEndTurn && { id: 'end-turn', label: 'Zug beenden', icon: SkipForward, onSelect: onEndTurn, disabled: !canEndTurn },
    onHome && { id: 'home', label: 'Eigene Fläche', icon: Home, onSelect: onHome },
    onRollDice && { id: 'dice', label: 'Würfeln', icon: Dices, children: diceActions(onRollDice) },
  ].filter(Boolean), [onUntapAndDraw, onEndTurn, canEndTurn, onHome, onRollDice]);

  const railRef = useRef(null);
  const moreRef = useRef(null);
  const heightsRef = useRef({});
  const [hiddenCount, setHiddenCount] = useState(0);
  const buttonIds = buttons.map((b) => b.id).join(',');
  const hasMore = more.length > 0 || hiddenCount > 0;

  const recompute = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    rail.querySelectorAll('[data-rail-item]').forEach((el) => {
      heightsRef.current[el.dataset.railItem] = el.getBoundingClientRect().height;
    });
    if (maxHeight == null) {
      setHiddenCount(0);
      return;
    }
    const style = window.getComputedStyle(rail);
    const gap = parseFloat(style.rowGap) || 0;
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    if (moreRef.current) heightsRef.current.more = moreRef.current.getBoundingClientRect().height;
    // „Mehr" erscheint erst, wenn etwas auszulagern ist; bis dahin gilt die Höhe eines Knopfes.
    const moreHeight = heightsRef.current.more ?? Math.max(0, ...Object.values(heightsRef.current));
    let used = padding + moreHeight;
    let fitting = 0;
    for (const id of buttonIds.split(',').filter(Boolean)) {
      const next = used + gap + (heightsRef.current[id] || 0);
      if (next > maxHeight) break;
      used = next;
      fitting += 1;
    }
    const total = buttonIds ? buttonIds.split(',').length : 0;
    setHiddenCount(total - fitting);
  }, [maxHeight, buttonIds]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute]);

  useLayoutEffect(() => {
    const rail = railRef.current;
    if (!rail || typeof ResizeObserver === 'undefined') return undefined; // jsdom
    const ro = new ResizeObserver(recompute);
    Array.from(rail.children).forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, [recompute, hiddenCount, hasMore]);

  const visible = buttons.slice(0, buttons.length - hiddenCount);
  const overflow = buttons.slice(buttons.length - hiddenCount);
  const moreItems = [
    ...overflow,
    ...more.map((entry, index) => (index === 0 && overflow.length > 0 ? { ...entry, separatorBefore: true } : entry)),
  ];

  const { select, dialog } = useActionConfirm();
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <nav ref={railRef} className="action-rail" aria-label="Aktionsleiste" data-testid="action-rail">
      {visible.map((button) => (button.id === 'dice' ? (
        <DicePopover key={button.id} onRoll={onRollDice}>
          <RailButton button={button} />
        </DicePopover>
      ) : (
        <RailButton key={button.id} button={button} onClick={() => button.onSelect()} />
      )))}
      {hasMore && (
        <div ref={moreRef} className="action-rail__more">
          {isMobile ? (
            <>
              <RailButton button={MORE_BUTTON} onClick={() => setSheetOpen(true)} />
              <ActionSheet open={sheetOpen} onOpenChange={setSheetOpen} title="Mehr" items={moreItems} />
            </>
          ) : (
            <ContextMenu trigger={<RailButton button={MORE_BUTTON} />}>
              <MoreEntries entries={moreItems} onSelect={select} />
            </ContextMenu>
          )}
        </div>
      )}
      {dialog}
    </nav>
  );
}

const MORE_BUTTON = { id: 'more', label: 'Mehr', icon: MoreVertical };

/**
 * Knopf der Leiste mit seinem Label als Tooltip; reicht Ref und Props durch, damit er als
 * Radix-Auslöser taugt. Radix weicht von rechts nach links aus, wenn die Leiste am rechten Rand steht.
 */
const RailButton = React.forwardRef(function RailButton({ button, ...rest }, ref) {
  const Icon = button.icon;
  return (
    <Tooltip label={button.label} side="right">
      <button
        {...rest}
        ref={ref}
        type="button"
        className="action-rail__button"
        data-rail-item={button.id === 'more' ? undefined : button.id}
        disabled={button.disabled}
      >
        <Icon size={20} aria-hidden />
      </button>
    </Tooltip>
  );
});

/** Einträge des „Mehr"-Menüs; Trennlinien aus `separatorBefore`, Untermenüs aus `children`. */
function MoreEntries({ entries, onSelect }) {
  return entries.map((entry, index) => (
    <React.Fragment key={entry.id}>
      {index > 0 && entry.separatorBefore && <ContextMenu.Separator />}
      {entry.children ? (
        <ContextMenu.Submenu icon={entry.icon} label={entry.label}>
          {entry.children.map((child) => (
            <ContextMenu.Item key={child.id} icon={child.icon} onSelect={() => onSelect(child)}>
              {child.label}
            </ContextMenu.Item>
          ))}
        </ContextMenu.Submenu>
      ) : (
        <ContextMenu.Item icon={entry.icon} danger={entry.danger} disabled={entry.disabled} onSelect={() => onSelect(entry)}>
          {entry.label}
        </ContextMenu.Item>
      )}
    </React.Fragment>
  ));
}
